import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Tag, X, Check } from "lucide-react";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";

export default function Checkout() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(true);
  const [cashfreeEnv, setCashfreeEnv] = useState("sandbox");
  const cashfreeRef = useRef(null);
  const [coupon, setCoupon] = useState({ code: "", applied: null, checking: false });
  const [form, setForm] = useState({
    full_name: "",
    certificate_name: "",
    email: "",
    mobile: "",
    address: "",
    state: "",
    city: "",
    pin_code: "",
    country: "India",
  });

  useEffect(() => {
    const raw = sessionStorage.getItem("teuralink_pending");
    if (!raw) {
      nav("/");
      return;
    }
    setPending(JSON.parse(raw));
    if (user)
      setForm((f) => ({
        ...f,
        email: user.email,
        full_name: user.name || "",
        certificate_name: f.certificate_name || user.name || "",
      }));
    api.get("/config").then(async (r) => {
      const cfg = r.data || {};
      setPaymentEnabled(!!cfg.payment_enabled);
      const env = cfg.cashfree_env === "production" ? "production" : "sandbox";
      setCashfreeEnv(env);
      if (cfg.payment_enabled) {
        try {
          cashfreeRef.current = await loadCashfree({ mode: env });
        } catch (err) {
          console.error("Cashfree SDK load failed", err);
        }
      }
    }).catch(() => {});
  }, [user, nav]);

  if (!user) {
    return (
      <div className="pt-32 max-w-md mx-auto text-center relative z-10">
        <h1 className="font-display text-3xl font-bold">Sign in to continue</h1>
        <p className="mt-3 text-white/60">You need to sign in to complete your registration.</p>
        <button className="btn-primary mt-6" onClick={login} data-testid="checkout-login">Sign in with Google</button>
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyCoupon = async () => {
    if (!coupon.code) return;
    setCoupon((c) => ({ ...c, checking: true }));
    try {
      const { data } = await api.post("/coupons/validate", {
        code: coupon.code.trim().toUpperCase(),
        amount_inr: pending.amount_inr,
      });
      setCoupon({
        code: data.code,
        checking: false,
        applied: {
          code: data.code,
          discount_inr: data.discount_inr,
          final_inr: data.final_inr,
          base_amount: pending.amount_inr,
        },
      });
      toast.success(`Coupon applied — you saved ₹${data.discount_inr}`);
    } catch (err) {
      setCoupon((c) => ({ ...c, checking: false, applied: null }));
      toast.error(err?.response?.data?.detail || "Invalid coupon");
    }
  };

  const removeCoupon = () => {
    setCoupon({ code: "", checking: false, applied: null });
    toast.info("Coupon removed");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pending) return;
    for (const k of ["full_name", "certificate_name", "email", "mobile", "address", "state", "city", "pin_code"]) {
      if (!form[k].trim()) {
        toast.error(`Please fill ${k.replace("_", " ")}`);
        return;
      }
    }
    if (!acceptTerms) {
      toast.error("Please accept the Terms & Conditions to continue");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...pending,
        coupon_code: coupon.applied?.code || null,
      };
      const { data: order } = await api.post("/orders/create", payload);
      // Sync UI to server-authoritative amount (final, after discount)
      if (order.amount_inr && order.amount_inr !== pending.amount_inr) {
        const synced = { ...pending, amount_inr: order.amount_inr };
        setPending(synced);
        sessionStorage.setItem("teuralink_pending", JSON.stringify(synced));
      }
      const verifyPayload = {
        cf_order_id: order.cf_order_id,
        checkout: form,
        order_meta: pending,
      };
      // Cache verify payload — the Cashfree Drop-in redirects the tab away, so on
      // return we complete verification from the confirmation page.
      sessionStorage.setItem(
        "teuralink_pending_verify",
        JSON.stringify(verifyPayload)
      );

      if (!cashfreeRef.current) {
        toast.error("Payment gateway is still loading. Please try again.");
        setBusy(false);
        return;
      }

      const result = await cashfreeRef.current.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: "_modal",
      });
      if (result?.error) {
        toast.error(result.error.message || "Payment cancelled");
        setBusy(false);
        return;
      }
      // Verify with our backend (server-to-server confirms Cashfree PAID + SUCCESS)
      try {
        const { data: confirmed } = await api.post("/orders/verify", verifyPayload);
        sessionStorage.removeItem("teuralink_pending");
        sessionStorage.removeItem("teuralink_pending_verify");
        toast.success("Registration complete!");
        nav(`/confirmation/${confirmed.registration_number}`);
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Payment verification failed");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => (typeof x === "string" ? x : x?.msg)).filter(Boolean).join(", ")
        : (typeof d === "string" ? d : null);
      toast.error(msg || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (!pending) return null;

  return (
    <div className="pt-28 pb-20 px-6 lg:px-10 relative z-10">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_380px] gap-8">
        <form onSubmit={submit} className="glass rounded-3xl p-8 lg:p-10">
          <div className="overline text-gold">Registration Details</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Complete your registration</h1>
          <p className="text-white/60 mt-2 text-sm">Your printed deed will be shipped to this address.</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <Field label="Full Name" value={form.full_name} onChange={set("full_name")} testid="co-name" />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="co-email" />
            <div className="sm:col-span-2">
              <Field
                label="Certificate Name"
                value={form.certificate_name}
                onChange={set("certificate_name")}
                testid="co-cert-name"
                maxLength={30}
                hint="This is the name that will be printed on your physical certificate — please enter it exactly as you'd like it to appear. Maximum 30 characters."
              />
            </div>
            <Field label="Mobile Number" value={form.mobile} onChange={set("mobile")} testid="co-mobile" />
            <Field label="PIN Code" value={form.pin_code} onChange={set("pin_code")} testid="co-pin" />
            <Field label="City" value={form.city} onChange={set("city")} testid="co-city" />
            <Field label="State" value={form.state} onChange={set("state")} testid="co-state" />
            <Field label="Country" value={form.country} onChange={set("country")} testid="co-country" />
            <div className="sm:col-span-2">
              <Field label="Complete Shipping Address" value={form.address} onChange={set("address")} testid="co-address" area />
            </div>
          </div>

          <div className="mt-6 p-4 border border-white/10 rounded-xl text-xs text-white/60 flex gap-3">
            <ShieldCheck className="w-4 h-4 text-gold shrink-0 mt-0.5" />
            Teuralink registrations are commemorative novelty items and do not confer legal ownership of land on any celestial body.
          </div>

          {/* Coupon Code — sits above the Payment Method as promised on the promo */}
          <div className="mt-6" data-testid="coupon-block">
            <div className="overline mb-3 text-white/60">Enter Coupon Code</div>
            {coupon.applied ? (
              <div className="flex items-center justify-between p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5" data-testid="coupon-applied">
                <div className="flex items-center gap-2 text-emerald-300 text-sm">
                  <Check className="w-4 h-4" />
                  <span className="font-mono-space">{coupon.applied.code}</span>
                  <span className="text-white/70">applied · saved ₹{coupon.applied.discount_inr.toLocaleString()}</span>
                </div>
                <button
                  type="button"
                  onClick={removeCoupon}
                  className="p-1 text-white/50 hover:text-white"
                  data-testid="coupon-remove"
                  title="Remove coupon"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  value={coupon.code}
                  onChange={(e) => setCoupon((c) => ({ ...c, code: e.target.value.toUpperCase() }))}
                  placeholder="ENTER CODE"
                  data-testid="coupon-input"
                  className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white font-mono-space text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={coupon.checking || !coupon.code}
                  className="btn-ghost !py-2 !px-5"
                  data-testid="coupon-apply"
                >
                  {coupon.checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* Payment method — Razorpay only. COD removed. */}

          <label
            className={`mt-5 flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
              acceptTerms
                ? "border-[#D4AF37]/50 bg-[#D4AF37]/5"
                : "border-white/10 bg-white/[0.02] hover:border-white/25"
            }`}
            data-testid="terms-accept-wrapper"
          >
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              data-testid="terms-accept-checkbox"
              className="mt-0.5 h-4 w-4 accent-[#D4AF37] cursor-pointer"
              required
            />
            <span className="text-sm text-white/80 leading-relaxed">
              I accept the{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold underline"
                data-testid="terms-link"
              >
                Terms &amp; Conditions
              </Link>
              {" "}and{" "}
              <Link
                to="/refund"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold underline"
              >
                Refund &amp; Cancellation Policy
              </Link>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={busy || !acceptTerms}
            className="btn-primary mt-6 w-full justify-center"
            data-testid="checkout-pay"
            style={{ opacity: acceptTerms ? 1 : 0.5 }}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {paymentEnabled
              ? `Pay ₹${(coupon.applied ? coupon.applied.final_inr : pending.amount_inr).toLocaleString()} & Register`
              : "Complete Registration"}
          </button>
          {!paymentEnabled && (
            <p className="mt-3 text-[11px] text-white/50 text-center" data-testid="payment-disabled-note">
              Payments are temporarily paused. Complete your registration now — our team will contact you if any fee applies.
            </p>
          )}
        </form>

        <aside className="glass rounded-3xl p-8 h-fit sticky top-24">
          <div className="overline text-gold">Order Summary</div>
          <div className="font-display text-2xl font-bold tracking-tight mt-2 capitalize">{pending.planet}</div>
          <div className="text-white/70 mt-1">{pending.marker_label}</div>
          <div className="text-xs text-white/50 font-mono-space mt-1">
            LAT {pending.lat.toFixed(2)}° · LON {pending.lon.toFixed(2)}°
          </div>

          <div className="border-t border-white/10 mt-6 pt-6 space-y-3 text-sm">
            <Row k="Plot Size" v={`${pending.acres} ${pending.acres === 1 ? "Acre" : "Acres"}`} />
            <Row k="Base Price" v={`₹${(coupon.applied?.base_amount || pending.amount_inr).toLocaleString()}`} />
            {coupon.applied && (
              <Row
                k={<span className="flex items-center gap-1.5"><Tag className="w-3 h-3" /> {coupon.applied.code}</span>}
                v={<span className="text-emerald-300">− ₹{coupon.applied.discount_inr.toLocaleString()}</span>}
              />
            )}
            <Row k="Currency" v="INR" />
            <Row k="Shipping" v="Included" />
          </div>

          <div className="border-t border-white/10 mt-6 pt-6 flex items-center justify-between">
            <div className="overline">Total</div>
            <div className="font-mono-space text-2xl text-gold" data-testid="checkout-total">
              ₹{(coupon.applied ? coupon.applied.final_inr : pending.amount_inr).toLocaleString()}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, area, hint, maxLength }) {
  const remaining = typeof maxLength === "number" ? maxLength - (value?.length || 0) : null;
  return (
    <label className="block">
      <span className="overline mb-2 text-white/60 flex items-center justify-between">
        <span>{label}</span>
        {remaining !== null && (
          <span
            className={`text-[10px] font-mono-space ${remaining <= 5 ? "text-amber-300" : "text-white/40"}`}
            data-testid={testid ? `${testid}-remaining` : undefined}
          >
            {remaining}/{maxLength}
          </span>
        )}
      </span>
      {area ? (
        <textarea
          value={value}
          onChange={onChange}
          rows={3}
          data-testid={testid}
          maxLength={maxLength}
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]/40"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          data-testid={testid}
          maxLength={maxLength}
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]/40"
        />
      )}
      {hint && (
        <span className="block mt-2 text-[11px] leading-relaxed text-gold/80" data-testid={testid ? `${testid}-hint` : undefined}>
          {hint}
        </span>
      )}
    </label>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between">
      <span className="text-white/50">{k}</span>
      <span className="text-white">{v}</span>
    </div>
  );
}

